// Chat with your own agent, from any page of the dashboard.
//
// The same agent that answers in Slack, on the same machine, with the same credentials and memory. The bubble is
// mounted once in the shell, so it keeps its conversation while you move around. A page can open it with a seed
// (see agentChatBus): a connection session. The dashboard starts it on the best way to connect the app (a sign-in it
// prepared, a key, the agent signing in itself, or, for an app no catalog knows, a key under a name the person can
// change) and offers every other way it has as a button beside the chat. A typed key goes to the vault through the
// install, never into a chat message. The session ends once the agent has been asked to check the connection; after
// that the chat is an ordinary one, and a key can still be replaced.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Expand, ExternalLink, KeyRound, Loader2, Lock, MessageSquare, RotateCcw, Send, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Markdown } from "./markdown";
import { readEventStream, reduceTurnEvent } from "./sse";
import { announceConnectionsChanged, onAgentChatOpen, openPopup } from "./agentChatBus";
import { ConnectionLogo } from "@/components/connections/ConnectionLogo";
import { AgentPicture } from "@/components/AgentPicture";
import { KEY_NAME, KeyFields, SECRET_VALUE, collectFields } from "@/components/connections/KeyForm";
import { askedFields, assertSameWorkspace, fieldKey, knownConnectionError, secureAuthorizationUrl, signInEndedMessage } from "@/components/connections/connectionsModel";
import "@/components/connections/connections.css";
import "./agentChat.css";

const OFFLINE_NOTE = "Your agent is offline right now, so it cannot answer here. It will pick up again when it is back.";
export const PROVIDER_NOTE = "Your agent could not reach its model just now. Send that again in a moment.";
// What answers when the model provider is unreachable (kit/plugins/xscale-alloy-credits).
const STAND_IN_MODEL = "alloy-out-of-credits";
const JOB_POLL_MS = 3000;
// How long an install is watched, and how many checks in a row may fail before the watch gives up: one dropped
// request is not the end of an install that is still going.
const JOB_WAIT_MS = 5 * 60 * 1000;
const JOB_POLL_FAILURES = 3;
const SEND_FAILED = "That message did not get through. Try again in a moment.";
const KEY_FAILED = "The key could not be installed. Try again.";
// The dashboard turns a connection session down for good with these, so the key field has nothing left to offer.
const SEED_REFUSED = new Set(["chat_seed_invalid", "connection_owner_or_admin_required"]);
const SUGGESTIONS = ["How did last week go?", "What can you do for me?", "Connect an app"];

const MCP_POLL_MS = 2000;
const HIGGSFIELD_POLL_MS = 3000;
// A sign-in is watched for as long as its link works: a native sign-in's link (Hermes on the agent's machine) for 30
// minutes, a Pipedream link's session at the HUB for 15; Higgsfield's wait is its own. After the first five minutes the
// checks slow down to one every ten seconds.
const SIGNIN_DEADLINE_MS = Object.freeze({ mcp: 30 * 60 * 1000, link: 15 * 60 * 1000, higgsfield: 5 * 60 * 1000 });
const SIGNIN_SLOW_AFTER_MS = 5 * 60 * 1000;
const SIGNIN_SLOW_POLL_MS = 10 * 1000;
const RENEW_FAILED = "A new sign-in link could not be made. Try again in a moment.";
// A link's account is asked of the HUB the way the Connections page watches a native sign-in: through a failed check or
// two, but three in a row end the watch, and so does an answer that cannot change while the page is open (signed out,
// not allowed, another workspace).
const SIGNIN_POLL_FAILURES = 3;
const SIGNIN_CHECK_FINAL = new Set(["tenant_payload_mismatch", "tenant_response_missing", "workspace_changed"]);
// The phases of an account no sign-in has just left (normalizeConnectionLifecycle): one that needs a new sign-in, one
// still being connected, one being removed. Any other account the HUB holds is one a callback recorded.
const NOT_SIGNED_IN_PHASES = new Set(["reauthorization_required", "connecting", "disconnecting"]);
const KEY_FLOWS = new Set(["key", "chat", "custom"]);
// The flows the Connections page keeps (Shopify's Partner request or its own sign-in, Meta's Facebook sign-in): the
// bubble shows no field for them, its session walks the person through the page's flow beside it (method "guided").
const GUIDED_FLOWS = new Set(["shopify_partner", "meta_signin", "shopify_oauth"]);
const isGuided = (seed) => seed?.method === "guided" || (!seed?.method && GUIDED_FLOWS.has(seed?.flow));
// A sign-in the dashboard could not prepare for one of these reasons cannot be prepared by trying again: the bubble says
// so instead of offering Start again. Any other failure to start the session is offered again.
const SEED_NOT_FOR_NOW = new Set(["mcp_not_enabled", "connection_provider_setup_required", "connection_managed", "connection_provider_not_startable", "connector_service_misconfigured"]);
// The ways a session can switch to with a button beside the chat (connectSeedEntry's alternatives).
const WAY_KINDS = new Set(["pipedream", "key", "agent_browser"]);
// The custom key panel's two inputs, by name in the panel's values.
const CUSTOM_NAME = "__name";
const CUSTOM_VALUE = "__value";
const PIPEDREAM_FAILED = "The Pipedream sign-in could not be prepared. Try again in a moment.";

/**
 * The person's first words, from the session the dashboard prepared (sent once it answers): a sign-in with its button
 * says the button does it, the agent's own sign-in asks what it needs, a key asks where it is, an app no catalog knows
 * asks how. Before the dashboard answers (or from one that names no method) the page's flow decides.
 */
export function openingTurn(seed) {
  const label = seed.label || seed.app;
  const method = seed.method || (seed.flow === "custom" ? "custom" : null);
  if (method === "custom") return `I want to connect ${label}. How do we do it?`;
  if (isGuided(seed)) return seed.flow === "shopify_oauth"
    ? `I'm connecting ${label} from the dashboard. It opened Shopify's approval for our store; what do I do there?`
    : seed.flow === "meta_signin"
      ? `I'm connecting ${label} from the dashboard. It sends me to Facebook to sign in; what do I do there?`
      : `I'm connecting ${label} with the form on the dashboard. What do I need to fill it in?`;
  if (method === "agent_browser") return `I'm connecting ${label} from the dashboard. It says you sign in for me; what do you need from me?`;
  if (method === "key") return `I'm connecting ${label} from the dashboard. Where do I find the key?`;
  if (seed.link || (!method && seed.flow && !KEY_FLOWS.has(seed.flow))) return `I'm connecting ${label} from the dashboard. I'll sign in with the button here.`;
  if (method) return `I'm connecting ${label} from the dashboard. What do I do next?`;
  return `I'm connecting ${label} from the dashboard. Where do I find the key?`;
}
/** What the person says when they pick the agent's own sign-in beside the chat. */
export function agentSignsInTurn(seed) {
  const label = seed.label || seed.app;
  return `Please sign in to ${label} for me in your own browser. What do you need from me?`;
}
export function signedInTurn(seed) {
  return `The ${seed.label || seed.app} sign-in is complete. Please check the connection.`;
}
/** A link's sign-in the HUB never saw finish: the agent checks for itself rather than taking it as done. */
export function notFinishedTurn(seed) {
  const label = seed.label || seed.app;
  return `The ${label} sign-in is not finished yet: the dashboard does not show the ${label} account. Please check the connection.`;
}
export function installedTurn(seed) {
  return `The ${seed.label || seed.app} key is installed through the dashboard. Please check it.`;
}
/** A guided form that went to X SCALE rather than onto the agent (Shopify's Partner request). */
export function submittedTurn(seed) {
  return `I sent the ${seed.label || seed.app} form through the dashboard. What happens next?`;
}

/** The account the HUB holds for an app (GET /api/connections/:app/status), as its phase and label, or null for none.
 *  The HUB keeps a link's account only once the provider sent the person back to the HUB's callback and the account
 *  was recorded, so this, not a window closing, is what says a link's sign-in finished. */
function hubAccount(payload) {
  const provider = payload?.provider;
  if (!provider?.lifecycle?.connected) return null;
  return { phase: String(provider.lifecycle.phase || ""), label: String(provider.account?.label || "") };
}

/** Whether `now` is an account this sign-in left: a signed-in one that was not there as it is now when the link opened
 *  (`before`). A reconnect replaces an account the HUB already showed, so that account unchanged does not count. */
function signedInSince(before, now) {
  if (!now || NOT_SIGNED_IN_PHASES.has(now.phase)) return false;
  return !before || before.phase !== now.phase || before.label !== now.label;
}

/** The way a session is on: its method, or before the dashboard answers (or for one that names none), the page's flow. */
function wayOf(seed) {
  if (seed?.method) return seed.method;
  if (seed?.flow === "custom") return "custom";
  if (GUIDED_FLOWS.has(seed?.flow)) return "guided";
  return KEY_FLOWS.has(seed?.flow) || !seed?.flow ? "key" : seed.flow;
}

/** The other ways a session offers, best first: the one it started on (when the person moved off it) and the
 *  alternatives the dashboard listed, never the one it is on now. */
function otherWays(minted, seed) {
  if (!minted || !seed || minted.app !== seed.app || !(minted.alternatives || []).length) return [];
  const kinds = [wayOf(minted), ...(minted.alternatives || []).map((way) => way?.kind).filter((kind) => WAY_KINDS.has(kind))];
  return [...new Set(kinds)].filter((kind) => kind !== wayOf(seed) && kind !== "custom");
}

/** The session's seed on another of its ways. The way it started on is the seed the dashboard prepared; a key shows
 *  that key's own fields; the agent's own sign-in has no link; a Pipedream sign-in has the link once it is minted. */
function seedOnWay(minted, kind, link = null) {
  if (kind === wayOf(minted)) return minted;
  const base = { ...minted, link: null, linkKind: null, flowId: null, note: null, verified: null };
  if (kind === "key") {
    const way = (minted.alternatives || []).find((alternative) => alternative?.kind === "key");
    return { ...base, method: "key", flow: "key", fields: way?.fields?.length ? way.fields : minted.fields };
  }
  if (kind === "agent_browser") return { ...base, method: "agent_browser", flow: "mcp", fields: [] };
  return { ...base, method: "pipedream", flow: "pipedream", fields: [], link, linkKind: link ? "popup" : null, verified: link ? true : null };
}

/** Where the sign-in panel starts for a seed the dashboard prepared. */
function signInStateFor(seed) {
  if (seed.link) return { status: "ready", error: "" };
  if (seed.method === "agent_browser") return { status: "agent_browser", error: "" };
  // A sign-in the dashboard could not prepare just now: Start again makes one.
  if (seed.startAgain && renewalOf(seed)) return { status: "unprepared", error: "" };
  const method = wayOf(seed);
  return method !== "key" && method !== "custom" ? { status: "unavailable", error: "" } : { status: "idle", error: "" };
}

/** The key name a custom app's field starts with: the one the dashboard prepared, else the app's own name. */
function customNameOf(seed) {
  const prepared = seed?.fields?.[0]?.envKey;
  if (typeof prepared === "string") return prepared;
  const name = `${String(seed?.app || "").toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "")}_API_KEY`;
  return KEY_NAME.test(name) ? name : "";
}

function initialOf(name = "") {
  const word = String(name).trim();
  return word ? word[0].toUpperCase() : "A";
}

function Message({ role, children }) {
  return (
    <div className={`achat-msg achat-msg--${role}`} data-testid={`chat-bubble-${role}`}>
      <div className="achat-msg__bubble">{children}</div>
    </div>
  );
}

/**
 * The other ways a connection session offers, as buttons: a Pipedream sign-in (minted when picked), a key, the agent
 * signing in itself, and the way the session started on once the person moved off it. `busy` is the way being prepared.
 */
function WaysRow({ ways, seed, agentName, busy = null, onPick }) {
  if (!ways.length) return null;
  const label = seed.label || seed.app;
  const words = { key: "Use a key instead", agent_browser: `Let ${agentName} sign in`, pipedream: "Sign in through Pipedream" };
  return (
    <div className="achat-ways" data-testid="agent-chat-signin-alternatives">
      <span className="achat-ways__label">Other ways:</span>
      {ways.map((kind) => (
        <button key={kind} type="button" className="achat-way" data-testid={`agent-chat-way-${kind}`} disabled={Boolean(busy)} onClick={() => onPick(kind)}>
          {busy === kind ? <Loader2 size={12} className="cx-spin" /> : kind === "key" ? <KeyRound size={12} /> : kind === "agent_browser" ? <MessageSquare size={12} /> : <ExternalLink size={12} />}
          {words[kind] || `Sign in to ${label}`}
        </button>
      ))}
    </div>
  );
}

/**
 * A flow the Connections page keeps (Shopify's Partner request or its own approval window, Meta's Facebook sign-in):
 * the bubble asks for nothing, the agent walks the person through it on the page beside the chat, and the page tells
 * the bubble when a form is done (a Meta sign-in takes the whole tab and comes back to the page connected).
 */
function GuidedPanel({ seed, agentName, onDismiss }) {
  const approval = seed.flow === "shopify_oauth";
  const facebook = seed.flow === "meta_signin";
  const title = approval ? `Approve ${seed.label} in its window` : facebook ? `Sign in with Facebook on the page` : `Fill in the ${seed.label} form on the page`;
  const hint = approval ? "The page shows when it is connected."
    : facebook ? `Pick ${seed.label} under Add connection: Meta's own login opens, and the page shows ${seed.label} connected when you are back.`
      : "What you type there goes to the dashboard, never into this chat.";
  return (
    <section className="achat-key" data-testid="agent-chat-guided-panel" data-flow={seed.flow}>
      <div className="achat-key__head">
        <ConnectionLogo app={seed.app} label={seed.label} size={34} />
        <div className="achat-key__copy">
          <p className="achat-key__title">{title}</p>
          <p className="achat-key__hint">{`${agentName} walks you through it here. ${hint}`}</p>
        </div>
        <button type="button" className="achat-key__dismiss" onClick={onDismiss}>Not now</button>
      </div>
    </section>
  );
}

/**
 * The masked key field of a connection session: one input per catalog field, the first one carries the test id. Once
 * the session is over (status "checked") it only says the key is installed and offers to replace it. `note` is what
 * the person should know about the install itself (a restart hint) and stays beside it.
 */
function KeyPanel({ seed, values, errors, status, error, note = "", onChange, onSubmit, onDismiss, onReplace, children = null }) {
  const fields = askedFields(seed);
  const firstKey = fieldKey(fields[0] || {});
  const installing = status === "installing";
  const checked = status === "checked";
  const installed = status === "done" || checked;
  return (
    <form className="achat-key" data-testid="agent-chat-key-panel" onSubmit={(event) => { event.preventDefault(); if (!installed) onSubmit(); }} autoComplete="off">
      <div className="achat-key__head">
        <ConnectionLogo app={seed.app} label={seed.label} size={34} />
        <div className="achat-key__copy">
          <p className="achat-key__title">{installed ? `${seed.label} key installed` : `Paste the ${seed.label} key here`}</p>
          <p className="achat-key__hint">{checked ? [note, "If it was the wrong key, replace it here."].filter(Boolean).join(" ") : installed ? (note || "Your agent is checking it now.") : "It goes straight to your agent's machine, never into the chat."}</p>
        </div>
        {checked ? <button type="button" className="achat-key__dismiss" data-testid="agent-chat-key-replace" onClick={onReplace}>Replace key</button> : null}
        {!installed ? <button type="button" className="achat-key__dismiss" onClick={onDismiss}>Not now</button> : null}
      </div>
      {installed ? (
        <span className="achat-key__status achat-key__status--done"><Check size={14} />Installed</span>
      ) : (
        <>
          <div className="achat-key__row">
            <KeyFields fields={fields.length ? fields : [{ name: `${seed.app}_key`, label: "Key", secret: true }]} values={values} errors={errors} onChange={onChange} testIdFor={(key) => (key === firstKey || !fields.length ? "agent-chat-key-field" : `agent-chat-key-field-${key}`)} />
          </div>
          <div className="achat-key__head">
            <span className="achat-key__lock"><Lock size={11} />Encrypted, installed within about a minute, never shown again.</span>
            <button type="submit" className="cx-btn cx-btn--primary cx-btn--sm" data-testid="agent-chat-key-submit" disabled={installing}>{installing ? <Loader2 size={13} className="cx-spin" /> : <Lock size={13} />}{installing ? "Installing…" : "Install key"}</button>
          </div>
          {status === "failed" ? <span className="achat-key__status achat-key__status--bad">{error || "The key could not be installed. Try again."}</span> : null}
          {children}
        </>
      )}
    </form>
  );
}

/**
 * The key field of an app no catalog knows: the key's name, filled in from the app and editable (the name its own docs
 * give), and the key itself, masked. The same thing as the Connections page's Something else form, beside the chat.
 */
function CustomKeyPanel({ seed, name, values, errors, status, error, note = "", onName, onChange, onSubmit, onDismiss, onReplace }) {
  const installing = status === "installing";
  const checked = status === "checked";
  const installed = status === "done" || checked;
  return (
    <form className="achat-key" data-testid="agent-chat-key-panel" data-kind="custom" onSubmit={(event) => { event.preventDefault(); if (!installed) onSubmit(); }} autoComplete="off">
      <div className="achat-key__head">
        <ConnectionLogo app={seed.app} label={seed.label} size={34} />
        <div className="achat-key__copy">
          <p className="achat-key__title">{installed ? `${seed.label} key installed` : `Paste the ${seed.label} key here`}</p>
          <p className="achat-key__hint">{checked ? [note, "If it was the wrong key, replace it here."].filter(Boolean).join(" ") : installed ? (note || "Your agent is checking it now.") : "Your agent says where to find it. It goes straight to your agent's machine, never into the chat."}</p>
        </div>
        {checked ? <button type="button" className="achat-key__dismiss" data-testid="agent-chat-key-replace" onClick={onReplace}>Replace key</button> : null}
        {!installed ? <button type="button" className="achat-key__dismiss" onClick={onDismiss}>Not now</button> : null}
      </div>
      {installed ? (
        <span className="achat-key__status achat-key__status--done"><Check size={14} />Installed</span>
      ) : (
        <>
          <div className="achat-key__row">
            <label className="cx-field">
              <span>Key name</span>
              <input type="text" data-testid="agent-chat-custom-key-name" value={name} placeholder="LOOP_API_TOKEN" onChange={(event) => onName(event.target.value.trim().toUpperCase())} autoComplete="off" spellCheck={false} />
              {errors[CUSTOM_NAME] ? <em>{errors[CUSTOM_NAME]}</em> : null}
            </label>
            <label className="cx-field">
              <span>Key</span>
              <input type="password" data-testid="agent-chat-key-field" value={values[CUSTOM_VALUE] || ""} onChange={(event) => onChange(CUSTOM_VALUE, event.target.value.trim())} autoComplete="off" spellCheck={false} />
              {errors[CUSTOM_VALUE] ? <em>{errors[CUSTOM_VALUE]}</em> : null}
            </label>
          </div>
          <div className="achat-key__head">
            <span className="achat-key__lock"><Lock size={11} />Encrypted, installed within about a minute, never shown again.</span>
            <button type="submit" className="cx-btn cx-btn--primary cx-btn--sm" data-testid="agent-chat-key-submit" disabled={installing}>{installing ? <Loader2 size={13} className="cx-spin" /> : <Lock size={13} />}{installing ? "Installing…" : "Install key"}</button>
          </div>
          {status === "failed" ? <span className="achat-key__status achat-key__status--bad">{error || "The key could not be installed. Try again."}</span> : null}
        </>
      )}
    </form>
  );
}

/** How a link this session already opened is replaced with a new one: a native sign-in is started again on the agent's
 *  machine ("mcp"), a Pipedream link is minted again ("pipedream"). The same for a sign-in the dashboard could not
 *  prepare when the session started (`startAgain`, no link yet). Null for a link that stays the one it is (Higgsfield's)
 *  and for no link at all otherwise. */
export function renewalOf(seed) {
  if (!seed) return null;
  if (seed.link && seed.flowId) return "mcp";
  if (seed.link) return seed.flow === "pipedream" ? "pipedream" : null;
  if (seed.startAgain) return seed.flow === "pipedream" ? "pipedream" : seed.flow === "mcp" ? "mcp" : null;
  return null;
}

/**
 * The sign-in panel of a connection session whose server minted a link: one button opens the provider in a small
 * window, the bubble watches for the sign-in to finish (Hermes' flow for a native sign-in, the dashboard's redeem for
 * Higgsfield, the HUB holding the account for a link), then asks the agent to check. `status`: preparing | ready |
 * waiting | done | failed (the button stays, to try again) | unavailable (no link: the note says why and the agent's
 * message says what works instead) | agent_browser (the agent signs in itself with the login given in the chat).
 * A link is never opened twice where it no longer works: a Pipedream link works once (a second open shows Pipedream's
 * "This link has expired"), and a native sign-in that failed has no link left. There the button is Start again
 * (`onRenew`), which makes a new link and turns back into Sign in. `children` is the row of the session's other ways.
 */
function SignInPanel({ seed, status, error, retry = false, agentName, onOpen, onRenew = null, onRetry = null, onDismiss, children = null }) {
  const preparing = status === "preparing";
  const waiting = status === "waiting";
  const agentBrowser = status === "agent_browser";
  const unavailable = status === "unavailable" || agentBrowser;
  const renewal = onRenew ? renewalOf(seed) : null;
  // Start again makes a new link: for a link already used (a Pipedream link works once, a native sign-in that ended),
  // and for a sign-in the dashboard could not prepare when the session started. A session that could not be started at
  // all is started again (`retry`). The button is never a dead Sign in with nothing behind it.
  const renew = renewal !== null && (status === "failed" || status === "unprepared" || (waiting && renewal === "pipedream"));
  const restart = !renew && retry && status === "failed" && Boolean(onRetry);
  return (
    <section className="achat-key" data-testid="agent-chat-signin-panel" data-status={status} aria-live="polite">
      <div className="achat-key__head">
        <ConnectionLogo app={seed.app} label={seed.label} size={34} />
        <div className="achat-key__copy">
          <p className="achat-key__title">{status === "done" ? `${seed.label} is signed in` : preparing ? `Preparing the ${seed.label} sign-in…` : agentBrowser ? `${agentName} signs in to ${seed.label} itself` : unavailable ? `${seed.label} needs another way` : `Sign in to ${seed.label}`}</p>
          <p className="achat-key__hint" data-testid="agent-chat-signin-note">{status === "done" ? `${agentName} is checking it now.` : (status === "unavailable" && error) || seed.note || (preparing ? "Your agent is getting the sign-in ready. About ten seconds." : agentBrowser ? `Give ${agentName} your ${seed.label} login here, and any code ${seed.label} sends; it signs in from its own machine.` : unavailable ? `${seed.label} cannot be signed into from here yet. ${agentName} will say what works.` : "Your agent keeps the sign-in on its own machine. Nothing goes through the chat.")}</p>
        </div>
        {status !== "done" ? <button type="button" className="achat-key__dismiss" onClick={onDismiss}>Not now</button> : null}
      </div>
      {status === "done" ? (
        <span className="achat-key__status achat-key__status--done"><Check size={14} />Signed in</span>
      ) : (
        <>
          {unavailable ? null : (
            <div className="achat-key__actions">
              <button type="button" className="cx-btn cx-btn--primary cx-btn--sm" data-testid="agent-chat-signin" disabled={preparing || (!seed.link && !renew && !restart)} onClick={renew ? onRenew : restart ? onRetry : onOpen}>
                {preparing ? <Loader2 size={13} className="cx-spin" /> : renew || restart ? <RotateCcw size={13} /> : <ExternalLink size={13} />}{renew || restart ? "Start again" : waiting ? "Open it again" : `Sign in to ${seed.label}`}
              </button>
              {waiting ? <span className="achat-key__status achat-key__status--busy" data-testid="agent-chat-signin-waiting"><Loader2 size={13} className="cx-spin" />Waiting for your sign-in…</span> : null}
              {status === "failed" ? <span className="achat-key__status achat-key__status--bad" data-testid="agent-chat-signin-error">{error || "The sign-in did not finish."}</span> : null}
              {seed.link && seed.verified === null && !waiting && status !== "failed" ? <span className="achat-key__lock" data-testid="agent-chat-signin-unverified">If the sign-in page refuses, tell {agentName}.</span> : null}
            </div>
          )}
          {children}
        </>
      )}
    </section>
  );
}

export function AgentChat() {
  const { api, activeClient, readOnly = false, readOnlyMessage } = useApp();
  const [state, setState] = useState({ available: false, online: false, agentName: "your agent" });
  const [open, setOpen] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [live, setLive] = useState(null);
  const [error, setError] = useState(null);
  const [seed, setSeed] = useState(null);
  // The app whose key this conversation installed and had checked: its connection session is over, the key can be replaced.
  const [installed, setInstalled] = useState(null);
  const [keyValues, setKeyValues] = useState({});
  const [keyErrors, setKeyErrors] = useState({});
  const [keyStatus, setKeyStatus] = useState("idle");
  const [keyError, setKeyError] = useState("");
  const [keyNote, setKeyNote] = useState("");
  // A connection session with a server-minted link: preparing | ready | waiting | done | failed.
  const [signIn, setSignIn] = useState({ status: "idle", error: "" });
  // What the HUB held for the app when a link was first opened ({ link, account: a promise of hubAccount }), so an
  // account that was already there is never taken for the one this sign-in left.
  const baselineRef = useRef(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  // The seed as of now, for an install that has waited minutes and then has to know whether its session still is.
  const seedRef = useRef(null);
  const changeSeed = useCallback((next) => { seedRef.current = next; setSeed(next); }, []);
  // The seed as the dashboard prepared it, whichever of its ways the person is on now (null outside a session).
  const mintedRef = useRef(null);
  // The chat this bubble talks in, as of now, and the app whose session is being created, if one is.
  const sessionRef = useRef(null);
  const preparingRef = useRef(null);
  // A connection session asked for before the bubble knew its agent could chat, started once it knows.
  const awaitingChatRef = useRef(null);
  const changeSession = useCallback((id) => { sessionRef.current = id; }, []);
  // The key's name in an app's custom key panel, once the person changed it (null: the name the session came with).
  const [customName, setCustomName] = useState(null);
  // The way the bubble is preparing (a Pipedream link being minted), so its button waits.
  const [wayBusy, setWayBusy] = useState(null);
  const slug = activeClient?.clientSlug ?? null;

  // Is chat switched on for this brand, and is the agent awake?
  useEffect(() => {
    let cancelled = false;
    if (!api?.getAgentChat || !slug) return undefined;
    api.getAgentChat()
      .then((payload) => { if (!cancelled) setState({ available: Boolean(payload?.available), online: Boolean(payload?.online), agentName: payload?.agentName || "your agent" }); })
      .catch(() => { if (!cancelled) setState({ available: false, online: false, agentName: "your agent" }); });
    return () => { cancelled = true; };
  }, [api, slug]);

  // A brand switch belongs to a different agent, so the conversation starts again.
  useEffect(() => { changeSession(null); setMessages([]); setLive(null); setError(null); changeSeed(null); mintedRef.current = null; setInstalled(null); setKeyValues({}); setKeyStatus("idle"); setCustomName(null); setSignIn({ status: "idle", error: "" }); }, [slug, changeSeed, changeSession]);

  useEffect(() => {
    // Not every environment implements scrolling; the panel still works without it.
    if (open) endRef.current?.scrollIntoView?.({ block: "end" });
  }, [messages, live, open, seed]);

  // `fresh` starts a new chat whatever this bubble talked in before (a page that opens a new connection session).
  // `message` may be a function of the session's seed: the opening turn of a connection session is said once the
  // dashboard answered with what it prepared, so the person's first words fit the way it chose.
  const sendTurn = useCallback(async (message, { seedForSession = null, fresh = false } = {}) => {
    setError(null);
    const deferred = typeof message === "function";
    if (!deferred) setMessages((rows) => [...rows, { role: "user", text: message }]);
    setLive({ text: "", tool: "", status: "running" });
    try {
      let id = fresh ? null : sessionRef.current;
      if (!id) {
        if (seedForSession && seedForSession.flow && !KEY_FLOWS.has(seedForSession.flow) && !GUIDED_FLOWS.has(seedForSession.flow)) setSignIn({ status: "preparing", error: "" });
        preparingRef.current = seedForSession?.app ?? null;
        let created;
        try {
          created = await api.createAgentChatSession(seedForSession ? { seed: { kind: seedForSession.kind, app: seedForSession.app, ...(seedForSession.way ? { way: seedForSession.way } : {}) } } : undefined);
        } finally {
          preparingRef.current = null;
        }
        id = created?.session?.id || created?.sessionId;
        if (!id) throw new Error("no_session");
        changeSession(id);
        // The server prepared the session's first way while creating it: the sign-in it minted, the key fields, the
        // other ways it offers.
        const minted = created?.session?.seed || created?.seed;
        if (seedForSession && seedRef.current?.app === seedForSession.app) {
          const current = seedRef.current;
          const next = minted ? {
            ...current, flow: minted.flow || current.flow, link: minted.link || null, linkKind: minted.linkKind || null, flowId: minted.flowId || null,
            note: minted.note || null, verified: minted.verified === undefined ? null : minted.verified, method: minted.method || null,
            alternatives: Array.isArray(minted.alternatives) ? minted.alternatives : [], fields: Array.isArray(minted.fields) && minted.fields.length ? minted.fields : current.fields,
            startAgain: minted.startAgain === true,
          } : current;
          mintedRef.current = next;
          changeSeed(next);
          setSignIn(minted ? signInStateFor(next) : { status: "idle", error: "" });
        } else if (seedForSession) {
          setSignIn({ status: "idle", error: "" });
        }
      }
      const text = deferred ? message(seedForSession && seedRef.current?.app === seedForSession.app ? seedRef.current : (seedForSession || {})) : message;
      if (deferred) setMessages((rows) => [...rows, { role: "user", text }]);
      // The server attaches the connection instructions to each turn that names its app.
      const response = await api.streamAgentChatTurn({ sessionId: id, message: text, ...(seedForSession ? { connect: { app: seedForSession.app } } : {}) });
      let turn = { text: "", tool: "", status: "running", model: "" };
      await readEventStream(response, (name, payload) => { turn = reduceTurnEvent(turn, name, payload); setLive({ ...turn }); });
      if (turn.status === "failed") throw new Error("run_failed");
      if (turn.model === STAND_IN_MODEL) {
        // The agent could not reach its model. That answer is remembered against this chat, so the next message
        // starts a fresh one rather than repeating the same failure forever.
        changeSession(null);
        setError(PROVIDER_NOTE);
        return;
      }
      setMessages((rows) => [...rows, { role: "agent", text: turn.text || "(no answer)" }]);
    } catch (cause) {
      // An answer from the dashboard carries a code and, as its message, the bare code: say the page's sentence for it.
      setError(cause?.code === "agent_unreachable" ? OFFLINE_NOTE : knownConnectionError(cause)?.message || SEND_FAILED);
      if (seedForSession && SEED_REFUSED.has(cause?.code)) { changeSeed(null); mintedRef.current = null; setInstalled(null); setSignIn({ status: "idle", error: "" }); }
      // A session whose sign-in could not be prepared, so the session never started: Start again starts it again, unless
      // the reason is one a new try cannot change (then the panel says so, with nothing to press).
      else if (seedForSession) {
        const said = knownConnectionError(cause)?.message || "The sign-in could not be prepared. Try again in a moment.";
        setSignIn((prev) => (prev.status === "preparing"
          ? (SEED_NOT_FOR_NOW.has(cause?.code) ? { status: "unavailable", error: said } : { status: "failed", error: said, retry: true })
          : prev));
      }
    } finally {
      setLive(null);
    }
  }, [api, changeSeed, changeSession]);

  const send = useCallback(async () => {
    const message = draft.trim();
    if (!message || live || readOnly) return;
    setDraft("");
    await sendTurn(message, { seedForSession: seed });
  }, [draft, live, readOnly, seed, sendTurn]);

  // Another part of the dashboard asked for the bubble: a connection session, or a draft to start from. The listener
  // is subscribed once and calls the handler of the latest render: re-subscribing whenever the handler changed left a
  // moment in a commit with no listener, and a page whose own effect asked in that same commit was never heard (the
  // Connections page's Start again link on a fresh load, when the bubble and the page learn the brand has chat at once).
  const openRequestRef = useRef(null);
  openRequestRef.current = (detail) => {
    // A guided form on the page is done (Shopify's Partner request sent, Meta's token installed, Shopify approved): the
    // session that walked the person through it ends with the agent checking. Nothing else listens for it.
    if (detail.finished?.app) {
      const current = seedRef.current;
      if (current?.app !== detail.finished.app || !isGuided(current) || !sessionRef.current) return;
      setOpen(true);
      const turn = detail.finished.outcome === "submitted" ? submittedTurn(current) : detail.finished.outcome === "signed_in" ? signedInTurn(current) : installedTurn(current);
      sendTurn(turn, { seedForSession: current }).then(() => { if (seedRef.current === current) { changeSeed(null); mintedRef.current = null; } });
      return;
    }
    setOpen(true);
    setNudge(true);
    if (detail.draft) { setDraft(detail.draft); setTimeout(() => inputRef.current?.focus?.(), 0); }
    if (detail.message && state.available) sendTurn(String(detail.message));
    if (detail.seed?.kind === "connect" && detail.seed.app) {
      // The app picked again while its session is open (or still being prepared) is that session: it comes forward,
      // and nothing starts over.
      if (seedRef.current?.app === detail.seed.app && (sessionRef.current || preparingRef.current === detail.seed.app)) {
        setTimeout(() => inputRef.current?.focus?.(), 0);
        return;
      }
      // A new app means a new session, so its instructions ride on the session from the start.
      changeSession(null); setMessages([]); setError(null);
      changeSeed(detail.seed); mintedRef.current = null; setInstalled(null); setKeyValues({}); setKeyErrors({}); setKeyStatus("idle"); setKeyError(""); setKeyNote(""); setCustomName(null); setWayBusy(null); setSignIn({ status: "idle", error: "" });
      // The first words wait for the session: they say the way the dashboard prepared. A page that asks before the
      // bubble knows its agent can chat (the Connections page's Start again link, on a fresh load) has its session
      // started as soon as it does.
      if (state.available) sendTurn(openingTurn, { seedForSession: detail.seed, fresh: true });
      else awaitingChatRef.current = detail.seed;
    }
  };
  useEffect(() => onAgentChatOpen((detail) => openRequestRef.current?.(detail)), []);
  useEffect(() => {
    const waiting = awaitingChatRef.current;
    if (!state.available || !waiting) return;
    awaitingChatRef.current = null;
    if (seedRef.current === waiting && !sessionRef.current && preparingRef.current === null) sendTurn(openingTurn, { seedForSession: waiting, fresh: true });
  }, [state.available, sendTurn]);
  useEffect(() => { if (!nudge) return undefined; const timer = setTimeout(() => setNudge(false), 700); return () => clearTimeout(timer); }, [nudge]);

  // The key goes to the vault and onto the agent; when the install is done the agent is asked to check it. An app no
  // catalog knows sends its key under the name in its panel, the way the page's Something else form does.
  const submitKey = useCallback(async () => {
    if (!seed || keyStatus === "installing") return;
    let install;
    let installedSeed = seed;
    if (wayOf(seed) === "custom") {
      const envKey = customName ?? customNameOf(seed);
      const value = keyValues[CUSTOM_VALUE] || "";
      const errors = {};
      if (!KEY_NAME.test(envKey)) errors[CUSTOM_NAME] = `Use capital letters, numbers and underscores, like ${customNameOf(seed) || "LOOP_API_TOKEN"}.`;
      if (!SECRET_VALUE.test(value)) errors[CUSTOM_VALUE] = value ? "The key must be a single line without spaces or quotes." : "The key is required.";
      setKeyErrors(errors);
      if (Object.keys(errors).length) return;
      install = () => api.addCustomAgentConnection(envKey, value);
      installedSeed = { ...seed, fields: [{ ...(seed.fields?.[0] || { label: "API key", secret: true, required: true, ask: "required" }), envKey }] };
    } else {
      const fields = askedFields(seed);
      const asked = fields.length ? fields : [{ name: `${seed.app}_key`, label: "Key", secret: true }];
      const result = collectFields(asked, keyValues);
      setKeyErrors(result.errors);
      if (Object.keys(result.errors).length) return;
      install = () => api.addAgentConnection(seed.app, result.values);
    }
    setKeyStatus("installing"); setKeyError("");
    try {
      const started = await install();
      const jobId = started?.jobId;
      let job = jobId ? null : { status: "done" };
      // Watched for five minutes at most. A failed job ends the watch, and so do three checks in a row that fail; a
      // check that gets through starts that count again.
      const giveUpAt = Date.now() + JOB_WAIT_MS;
      let failures = 0;
      while (jobId && !["done", "failed"].includes(job?.status) && Date.now() < giveUpAt) {
        await new Promise((resolve) => setTimeout(resolve, JOB_POLL_MS));
        try {
          job = (await api.getAgentConnectionJob(jobId))?.job || null;
          failures = 0;
        } catch (cause) {
          failures += 1;
          if (failures >= JOB_POLL_FAILURES) throw cause;
        }
      }
      if (job?.status !== "done") throw new Error(job?.error || "The install did not finish.");
      // A key the agent wrote itself is live from its next message; a tool server that read it at start needs a restart.
      setKeyNote(started?.restartNeeded ? "Live from its next message; its tools need a quick restart to see it." : "");
      setKeyStatus("done"); setKeyValues({});
      await sendTurn(installedTurn(seed), { seedForSession: seed });
      // That check was the session's last turn: from here on turns carry no connect, and the panel only offers to
      // replace the key. A session that changed while the install ran (another app, Not now) is left as it is.
      if (seedRef.current === seed) { changeSeed(null); mintedRef.current = null; setInstalled(installedSeed); setCustomName(null); }
    } catch (cause) {
      // The same for the install: a sentence this panel threw (the job's own error) is kept, an answer from the
      // dashboard is said in the page's words, and one the page has no words for gets the generic sentence.
      setKeyStatus("failed");
      setKeyError(cause?.code ? knownConnectionError(cause)?.message || KEY_FAILED : cause?.message || KEY_FAILED);
    }
  }, [api, seed, keyStatus, keyValues, customName, sendTurn, changeSeed]);

  // A session whose sign-in could not be prepared never started: Start again starts it again, as the page's pick did.
  const restartSession = useCallback(() => {
    const current = seedRef.current;
    if (!current || sessionRef.current) return;
    setError(null);
    sendTurn(openingTurn, { seedForSession: current, fresh: true });
  }, [sendTurn]);

  // Opens the minted link once, in a small window, and starts watching for the sign-in to finish. Once: the window is
  // opened with noopener, which never hands a window back, so a fallback "open it in a tab when no window came back"
  // opened the same link a second time on every click, and a Pipedream link works only once (GrowthOS Test Lab,
  // 2026-10-13: Google Ads answered "This link has expired").
  const openSignIn = useCallback(() => {
    const link = seedRef.current?.link;
    if (!link) return;
    openPopup(link);
    setSignIn({ status: "waiting", error: "", startedAt: Date.now() });
  }, []);

  // Start again: a new link for the session's sign-in, made the way its own way makes one (a native sign-in through
  // the dashboard's MCP route, which hands back the sign-in still waiting on the agent's machine if there is one; a
  // Pipedream link through the dashboard's start). The panel then offers Sign in with the new link. The account the
  // HUB held when this session's first link opened stays the baseline, so a sign-in finished late on an earlier link
  // still counts as this session's.
  const startAgain = useCallback(async () => {
    const current = seedRef.current;
    const renewal = renewalOf(current);
    if (!renewal) return;
    setSignIn({ status: "preparing", error: "" });
    try {
      let next;
      if (renewal === "mcp") {
        const flow = assertSameWorkspace(await api.connectMcp(current.app), activeClient);
        const link = secureAuthorizationUrl(flow?.authorizationUrl);
        if (!flow?.flowId || !link) throw new Error("no_link");
        next = { ...current, link, flowId: flow.flowId, startAgain: false };
      } else {
        const answer = assertSameWorkspace(await api.startConnection(current.app), activeClient);
        const link = secureAuthorizationUrl(answer?.connectLinkUrl || answer?.authorizationUrl);
        if (!link) throw new Error("no_link");
        next = { ...current, link, startAgain: false };
      }
      if (seedRef.current !== current) return;
      if (current.link && baselineRef.current?.link === current.link) baselineRef.current = { ...baselineRef.current, link: next.link };
      const minted = mintedRef.current;
      if (minted && minted.app === current.app && minted.link === current.link) mintedRef.current = { ...minted, link: next.link, flowId: next.flowId ?? minted.flowId, startAgain: false };
      changeSeed(next);
      setSignIn({ status: "ready", error: "" });
    } catch (cause) {
      if (seedRef.current === current) setSignIn({ status: "failed", error: knownConnectionError(cause)?.message || RENEW_FAILED });
    }
  }, [api, activeClient, changeSeed]);

  // The sign-in finished: the page shows the card now and reloads, the agent is asked to check, the session is over.
  const finishSignIn = useCallback(async ({ toolCount = null } = {}) => {
    const current = seedRef.current;
    if (!current) return;
    setSignIn({ status: "done", error: "" });
    announceConnectionsChanged({ app: current.app, label: current.label, kind: current.flow === "mcp" ? "mcp" : current.flow === "higgsfield" ? "higgsfield" : "pipedream", toolCount });
    await sendTurn(signedInTurn(current), { seedForSession: current });
    if (seedRef.current === current) { changeSeed(null); mintedRef.current = null; setInstalled(null); setSignIn({ status: "idle", error: "" }); }
  }, [sendTurn, changeSeed]);

  // A link's sign-in the HUB has not seen finish: the panel says so beside the link, to try again, and the agent is
  // told, so it checks for itself rather than taking the sign-in as done. The session stays open for the next try.
  const notFinishedSignIn = useCallback(async (error) => {
    const current = seedRef.current;
    if (!current) return;
    setSignIn({ status: "failed", error });
    await sendTurn(notFinishedTurn(current), { seedForSession: current });
  }, [sendTurn]);

  // While a sign-in is waited on: a native sign-in is asked of Hermes and Higgsfield's code of the dashboard, every
  // few seconds for five minutes at most; a link's account is asked of the HUB the same way, through a failed check or
  // two. Closing the session stops the watch.
  useEffect(() => {
    if (signIn.status !== "waiting" || !seed) return undefined;
    let stopped = false, timer = null, failures = 0;
    const startedAt = signIn.startedAt || Date.now();
    const label = seed.label || seed.app;
    const watch = seed.flowId && typeof api?.getMcpFlow === "function" ? "mcp"
      : seed.flow === "higgsfield" && typeof api?.completeHiggsfieldConnection === "function" ? "higgsfield"
        : "link";
    // What the HUB held for the app as the link first opened: read once per link, a failed read being no account.
    if (watch === "link" && baselineRef.current?.link !== seed.link) {
      baselineRef.current = { link: seed.link, account: (async () => hubAccount(await api.getConnectionStatus(seed.app)))().catch(() => null) };
    }
    const tick = async () => {
      if (stopped) return;
      if (Date.now() - startedAt > SIGNIN_DEADLINE_MS[watch]) {
        if (watch === "link") { await notFinishedSignIn(`The ${label} sign-in is not finished yet. Try again.`); return; }
        setSignIn({ status: "failed", error: watch === "mcp" ? "The sign-in took too long. Start again when you are ready." : "The sign-in took too long. Open it again when you are ready." }); return;
      }
      if (watch === "mcp") {
        try {
          const flow = await api.getMcpFlow(seed.flowId);
          if (stopped) return;
          if (flow?.status === "approved") { await finishSignIn({ toolCount: Number.isFinite(flow.toolCount) ? flow.toolCount : null }); return; }
          // How it ended, in the dashboard's words: never what the agent's machine or the provider said.
          if (flow?.status === "error") { setSignIn({ status: "failed", error: signInEndedMessage(flow.outcome, label) }); return; }
        } catch (cause) {
          if (stopped) return;
          // A 404 is the agent no longer holding the sign-in (mcp_flow_expired): it has expired, and its link with it.
          if (cause?.status && cause.status < 500) { setSignIn({ status: "failed", error: knownConnectionError(cause)?.message || "The sign-in could not be checked." }); return; }
        }
      } else if (watch === "higgsfield") {
        // Higgsfield's device code is redeemed by the dashboard: 202 pending keeps waiting, 200 installing is done, 410 expired.
        try {
          const result = await api.completeHiggsfieldConnection();
          if (stopped) return;
          if (result?.status === "installing") { await finishSignIn(); return; }
          if (result?.status && result.status !== "pending") { setSignIn({ status: "failed", error: "The connection service returned an unexpected Higgsfield status." }); return; }
        } catch (cause) {
          if (stopped) return;
          if (cause?.status === 410) { setSignIn({ status: "failed", error: "That Higgsfield link expired. Pick Higgsfield again for a new one." }); return; }
          if (cause?.status && cause.status < 500) { setSignIn({ status: "failed", error: knownConnectionError(cause)?.message || "The sign-in could not be checked." }); return; }
        }
      } else {
        // A link (Pipedream's) is done once the HUB holds the account it left. A window that closed, or a link that
        // opened in a tab of its own (a window opened with noopener is never handed back), says nothing about whether
        // the provider sent the person back to the HUB's callback, or whether the HUB kept the account.
        try {
          const before = await baselineRef.current?.account;
          const now = hubAccount(await api.getConnectionStatus(seed.app));
          if (stopped) return;
          failures = 0;
          if (signedInSince(before, now)) { await finishSignIn(); return; }
        } catch (cause) {
          if (stopped) return;
          failures += 1;
          if (failures >= SIGNIN_POLL_FAILURES || cause?.status === 401 || cause?.status === 403 || SIGNIN_CHECK_FINAL.has(cause?.code)) {
            await notFinishedSignIn(`The dashboard could not check the ${label} sign-in. Try again in a moment.`); return;
          }
        }
      }
      timer = setTimeout(tick, pollDelay());
    };
    const pollDelay = () => (Date.now() - startedAt >= SIGNIN_SLOW_AFTER_MS ? SIGNIN_SLOW_POLL_MS : seed.flow === "higgsfield" ? HIGGSFIELD_POLL_MS : MCP_POLL_MS);
    timer = setTimeout(tick, pollDelay());
    return () => { stopped = true; clearTimeout(timer); };
  }, [signIn.status, signIn.startedAt, seed, api, finishSignIn, notFinishedSignIn]);

  // Replacing the key opens the same app's connection session again: its next check names the app once more.
  const replaceKey = useCallback(() => {
    if (!installed) return;
    changeSeed(installed); setKeyValues({}); setKeyErrors({}); setKeyStatus("idle"); setKeyError(""); setCustomName(null);
  }, [installed, changeSeed]);

  // The person picked another of the session's ways beside the chat. A key shows its own masked field; the agent's own
  // sign-in is asked of the agent at once (it needs the login); a Pipedream sign-in is minted through the dashboard's own
  // start, then waits for the Sign in button like any link; the way the session started on is the seed it came with.
  const chooseWay = useCallback(async (kind) => {
    const minted = mintedRef.current;
    const before = seedRef.current;
    if (!minted || !before || before.app !== minted.app) return;
    setKeyValues({}); setKeyErrors({}); setKeyStatus("idle"); setKeyError(""); setError(null);
    if (kind === wayOf(minted)) { changeSeed(minted); setSignIn(signInStateFor(minted)); return; }
    if (kind === "key") { changeSeed(seedOnWay(minted, "key")); setSignIn({ status: "idle", error: "" }); return; }
    if (kind === "agent_browser") {
      const next = seedOnWay(minted, "agent_browser");
      changeSeed(next); setSignIn({ status: "agent_browser", error: "" });
      await sendTurn(agentSignsInTurn(next), { seedForSession: next });
      return;
    }
    if (kind !== "pipedream") return;
    setWayBusy("pipedream");
    try {
      const answer = assertSameWorkspace(await api.startConnection(minted.app), activeClient);
      const link = secureAuthorizationUrl(answer?.connectLinkUrl || answer?.authorizationUrl);
      if (!link) throw new Error("no_link");
      if (seedRef.current !== before) return;
      changeSeed(seedOnWay(minted, "pipedream", link)); setSignIn({ status: "ready", error: "" });
    } catch (cause) {
      if (seedRef.current === before) setError(knownConnectionError(cause)?.message || PIPEDREAM_FAILED);
    } finally {
      setWayBusy(null);
    }
  }, [api, activeClient, sendTurn, changeSeed]);

  if (!state.available) return null;
  const stateWord = live ? "Thinking…" : state.online ? "Online" : "Offline";
  const stateTone = live ? "busy" : state.online ? "on" : "off";

  return (
    <>
      {!open && (
        <button type="button" onClick={() => setOpen(true)} data-testid="agent-chat-launcher" className={`achat-launcher${nudge ? " achat-launcher--nudge" : ""}`} aria-label={`Chat with ${state.agentName}`}>
          <span className="achat-avatar"><AgentPicture src={activeClient?.agentImageUrl} fallback={initialOf(state.agentName)} /><span className={`achat-avatar__dot${state.online ? " achat-avatar__dot--on" : ""}`} aria-hidden="true" /></span>
          <span className="achat-launcher__label">Chat with {state.agentName}</span>
          <MessageSquare size={16} aria-hidden="true" />
        </button>
      )}

      {open && (
        <section data-testid="agent-chat-panel" aria-label={`Chat with ${state.agentName}`} className="achat-window">
          <header className="achat-head">
            <span className="achat-avatar"><AgentPicture src={activeClient?.agentImageUrl} fallback={initialOf(state.agentName)} /><span className={`achat-avatar__dot${state.online ? " achat-avatar__dot--on" : ""}`} aria-hidden="true" /></span>
            <div className="achat-head__copy">
              <p className="achat-head__name">{state.agentName}</p>
              <p className={`achat-head__state achat-head__state--${stateTone}`} data-testid="agent-chat-state">{stateWord}</p>
            </div>
            <Link to="/agent/chat" className="achat-head__btn" aria-label="Open the full chat" title="Open the full chat" data-testid="agent-chat-expand"><Expand size={15} aria-hidden="true" /></Link>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close chat" data-testid="agent-chat-close" className="achat-head__btn"><X size={16} aria-hidden="true" /></button>
          </header>

          <div className="achat-list">
            {messages.length === 0 && !live && (
              <div className="achat-empty" data-testid="agent-chat-empty">
                <span className="achat-avatar" style={{ width: 44, height: 44, background: "rgba(0,59,141,0.1)", color: "#003B8D", fontSize: 18 }}><AgentPicture src={activeClient?.agentImageUrl} fallback={initialOf(state.agentName)} /></span>
                <p className="achat-empty__title">Ask {state.agentName} anything</p>
                <p className="achat-empty__text">The same agent as in Slack: your numbers, your campaigns, a task you want done.</p>
                <div className="achat-chips">
                  {SUGGESTIONS.map((text) => <button key={text} type="button" className="achat-chip" onClick={() => { setDraft(text === "Connect an app" ? "I want to connect " : text); inputRef.current?.focus?.(); }}>{text}</button>)}
                </div>
              </div>
            )}
            {messages.map((row, index) => <Message key={index} role={row.role}>{row.role === "agent" ? <Markdown text={row.text} /> : row.text}</Message>)}
            {live && (
              <Message role="agent">
                {live.text ? <Markdown text={live.text} /> : null}
                <span className="achat-typing" data-testid="agent-chat-working"><span className="achat-typing__dots" aria-hidden="true"><i /><i /><i /></span>{live.tool ? `Working — ${live.tool}` : "Thinking…"}</span>
              </Message>
            )}
            {error && <p className={error === PROVIDER_NOTE ? "achat-note" : "achat-error"} data-testid="agent-chat-error">{error}</p>}
            <div ref={endRef} />
          </div>

          {seed && isGuided(seed)
            ? <GuidedPanel seed={seed} agentName={state.agentName} onDismiss={() => { changeSeed(null); mintedRef.current = null; }} />
            : seed && wayOf(seed) === "custom"
            ? <CustomKeyPanel seed={seed} name={customName ?? customNameOf(seed)} values={keyValues} errors={keyErrors} status={keyStatus} error={keyError} note={keyNote} onName={setCustomName} onChange={(key, value) => setKeyValues((prev) => ({ ...prev, [key]: value }))} onSubmit={submitKey} onDismiss={() => { changeSeed(null); mintedRef.current = null; }} />
            : seed && seed.method !== "key" && (seed.link || ["preparing", "waiting", "done", "failed", "unavailable", "unprepared", "agent_browser"].includes(signIn.status) || (seed.flow && !KEY_FLOWS.has(seed.flow)))
              ? <SignInPanel seed={seed} status={signIn.status === "idle" ? (seed.link ? "ready" : "preparing") : signIn.status} error={signIn.error} retry={Boolean(signIn.retry)} agentName={state.agentName} onOpen={openSignIn} onRenew={startAgain} onRetry={restartSession} onDismiss={() => { changeSeed(null); mintedRef.current = null; setSignIn({ status: "idle", error: "" }); }}>
                  <WaysRow ways={otherWays(mintedRef.current, seed)} seed={seed} agentName={state.agentName} busy={wayBusy} onPick={chooseWay} />
                </SignInPanel>
              : seed ? (
                <KeyPanel seed={seed} values={keyValues} errors={keyErrors} status={keyStatus} error={keyError} note={keyNote} onChange={(key, value) => setKeyValues((prev) => ({ ...prev, [key]: value }))} onSubmit={submitKey} onDismiss={() => { changeSeed(null); mintedRef.current = null; }}>
                  {keyStatus === "installing" ? null : <WaysRow ways={otherWays(mintedRef.current, seed)} seed={seed} agentName={state.agentName} busy={wayBusy} onPick={chooseWay} />}
                </KeyPanel>
              )
              : installed ? (wayOf(installed) === "custom"
                ? <CustomKeyPanel seed={installed} name="" values={{}} errors={{}} status="checked" error="" note={keyNote} onName={() => {}} onChange={() => {}} onSubmit={() => {}} onReplace={replaceKey} />
                : <KeyPanel seed={installed} values={{}} errors={{}} status="checked" error="" note={keyNote} onChange={() => {}} onSubmit={() => {}} onReplace={replaceKey} />) : null}

          <form className="achat-composer" onSubmit={(event) => { event.preventDefault(); send(); }}>
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); send(); } }}
              rows={1}
              placeholder={readOnly ? readOnlyMessage : state.online ? `Message ${state.agentName}…` : OFFLINE_NOTE}
              disabled={Boolean(live) || readOnly}
              data-testid="agent-chat-input"
              aria-label="Message your agent"
              className="achat-composer__input"
            />
            <button type="submit" disabled={Boolean(live) || readOnly || !draft.trim()} data-testid="agent-chat-send" aria-label="Send" className="achat-composer__send"><Send size={16} aria-hidden="true" /></button>
          </form>
        </section>
      )}
    </>
  );
}

export default AgentChat;
